import React from "react";
import { AppFlowLoginButton, PageContainer } from "../common/CommonStyles";
import { t } from "i18next";
import {
  AccountContext,
  RetryBehavior,
} from "../common/context/AccountContext";
import { CLIENT_TYPE } from "../common/api";
import { useHistory, useLocation, Link, useParams } from "react-router-dom";
import { RetryContext } from "../common/context/RetryContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import {
  AnalyticsContext,
  LoginWithOneTimeTokenFailedEvent,
  LoginWithOneTimeTokenSuccessfulEvent,
  ViewLogInWithOneTimeTokenEvent,
} from "../common/context/AnalyticsContext";

const isSameSitePath = (path: string) =>
  path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");

export const OneTimeTokenLoginPage: React.FC = () => {
  const location = useLocation();
  const { token: initialToken }: { token?: string } = useParams<{
    token?: string;
  }>();
  const [error, setError] = React.useState("");
  const { login, makeRequest } = React.useContext(AccountContext);
  const history = useHistory();
  const { showRetry } = React.useContext(RetryContext);
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    // Immediately hide the token from the URL into the location state
    if (initialToken !== undefined) {
      logEvent(new ViewLogInWithOneTimeTokenEvent());
      const next = new URLSearchParams(location.search).get("next");
      history.replace("/account/login/token/", {
        ...(location.state || {}),
        token: initialToken,
        ...(next !== null && isSameSitePath(next) && { next }),
      });
    } else if (location.state?.token !== undefined) {
      const token = location.state?.token;
      startLoading();
      makeRequest(
        "auth/one-time-token/login/",
        "POST",
        RetryBehavior.RETRY_EXCEPTION_EXPOSE_NON_SUCCESS,
        false,
        { token, client_type: CLIENT_TYPE }
      ).then((response) => {
        if (response.body) {
          stopLoading();
          login(response.body.access_token, response.body.user);
          logEvent(new LoginWithOneTimeTokenSuccessfulEvent());
          history.replace(location.state?.next ?? "/", { fromLogin: true });
        } else {
          stopLoading();
          setError(
            response.errorBody?.non_field_errors?.[0] ||
              t("one_time_token_login_unknown_error")
          );
          logEvent(new LoginWithOneTimeTokenFailedEvent());
        }
      });
    }
    else {
      // Shouldn't be here without a token, so redirect to the regular login page
      history.replace("/account/login/");
    }
  }, [
    initialToken,
    logEvent,
    history,
    login,
    makeRequest,
    showRetry,
    startLoading,
    stopLoading,
    location,
  ]);

  return (
    <PageContainer>
      {error && (
        <>
          <div className="alert alert-danger" role="alert">
            <h6 className="alert-heading">{t("error_alert_title")}</h6>
            <hr />
            <p>{error}</p>
            <p>{t("one_time_token_login_failed")}</p>
          </div>
          <div className="text-center">
            <Link to="/account/login/">
              <AppFlowLoginButton>
                {t("navigation_login")}
              </AppFlowLoginButton>
            </Link>
          </div>
        </>
      )}
    </PageContainer>
  );
};
